/**
 * Formato de cara al comerciante.
 *
 * Los importes viajan en centavos y se muestran en pesos con separador de miles y coma
 * decimal. Nada de esto toca lo que se le manda a la API, que quiere punto decimal y sin
 * separadores.
 */

const MONEDA = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});

const NUMERO = new Intl.NumberFormat('es-AR');

const FECHA = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  // Explícito y no confiando en el locale: en es-AR ya son 24 horas, pero un "9:30 p. m."
  // en una app de precios argentina se lee como un error de la app.
  hourCycle: 'h23',
});

/** Centavos → `$ 25.000,00`. Guion cuando no hay precio. */
export function pesos(centavos: number | null): string {
  if (centavos === null) return '—';
  return MONEDA.format(centavos / 100);
}

export function numero(valor: number): string {
  return NUMERO.format(valor);
}

const SOLO_FECHA = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** Siempre en hora de Buenos Aires: adentro todo está en UTC. */
export function fechaHora(iso: string | null): string {
  if (iso === null) return '—';
  return FECHA.format(new Date(iso));
}

/** Igual que `fechaHora` pero sin la hora, para plazos que se cuentan en días. */
export function fecha(iso: string | null): string {
  if (iso === null) return '—';
  return SOLO_FECHA.format(new Date(iso));
}

// --- Fechas relativas --------------------------------------------------------

/**
 * El día del calendario **en Buenos Aires**, como `2026-08-17`.
 *
 * Es la única forma correcta de preguntar "¿esto fue hoy?". Comparar contra el día del
 * navegador daría "ayer" para una corrida de las 22:30 vista desde España, y comparar
 * contra UTC daría "mañana" para una de las 21:30 de acá.
 */
const DIA_EN_BUENOS_AIRES = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Argentina/Buenos_Aires',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Sólo la hora, 24 horas.
 *
 * `hourCycle: 'h23'` y no `hour12: false`: con `hour12` algunos ICU devuelven "24:15" para
 * las 00:15.
 */
const SOLO_HORA = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const DIA_DE_SEMANA = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  weekday: 'long',
});

const UN_DIA_MS = 24 * 60 * 60 * 1000;

const DIA_Y_MES = new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  day: '2-digit',
  month: '2-digit',
});

/**
 * "17/08 a las 16:12". Para confirmar una acción, no para listar.
 *
 * Acá no van fechas relativas a propósito: "Hoy" alcanza para encontrar una corrida en una
 * lista, pero cuando se está por reescribir precios lo que hace falta es el dato exacto
 * contra el que comparar.
 */
export function fechaCortaConHora(iso: string | null): string {
  if (iso === null) return '—';
  const cuando = new Date(iso);
  return `${DIA_Y_MES.format(cuando)} a las ${SOLO_HORA.format(cuando)}`;
}

/**
 * "Hoy 16:21", "Ayer 09:30", "Martes 09:30", y fecha completa a partir de los 7 días.
 *
 * *** Por qué relativa. *** En el historial, "17/08/2026 16:21" obliga a hacer la cuenta
 * de qué tan reciente es. "Hoy 16:21" se entiende sin pensar, que es lo que hace falta
 * cuando alguien entra apurado a ver si la actualización que lanzó recién terminó bien.
 *
 * A partir de la semana el día de la semana deja de ser único —"martes" podría ser
 * cualquiera de dos— y pasa a la fecha completa.
 */
export function fechaHoraRelativa(iso: string | null, ahora: Date = new Date()): string {
  if (iso === null) return '—';
  const cuando = new Date(iso);
  const hora = SOLO_HORA.format(cuando);

  const diaDe = DIA_EN_BUENOS_AIRES.format(cuando);
  const diaHoy = DIA_EN_BUENOS_AIRES.format(ahora);
  if (diaDe === diaHoy) return `Hoy ${hora}`;
  if (diaDe === DIA_EN_BUENOS_AIRES.format(new Date(ahora.getTime() - UN_DIA_MS))) {
    return `Ayer ${hora}`;
  }

  // Los días se cuentan sobre el calendario de Buenos Aires y no sobre la diferencia en
  // milisegundos: una corrida de hace 6 días y 23 horas y otra de hace 7 días y 1 hora
  // pueden caer en el mismo día de la semana, y ahí "Martes" sería ambiguo.
  for (let atras = 2; atras < 7; atras += 1) {
    if (diaDe === DIA_EN_BUENOS_AIRES.format(new Date(ahora.getTime() - atras * UN_DIA_MS))) {
      const dia = DIA_DE_SEMANA.format(cuando);
      return `${dia[0]!.toLocaleUpperCase('es-AR')}${dia.slice(1)} ${hora}`;
    }
  }

  return FECHA.format(cuando);
}

export function diferencia(actual: number | null, nuevo: number | null): string {
  if (actual === null || nuevo === null) return '—';
  const delta = nuevo - actual;
  const signo = delta > 0 ? '+' : '';
  return `${signo}${MONEDA.format(delta / 100)}`;
}

export function porcentaje(actual: number | null, nuevo: number | null): string {
  if (actual === null || nuevo === null || actual === 0) return '—';
  const pct = ((nuevo - actual) / Math.abs(actual)) * 100;
  const signo = pct > 0 ? '+' : '';
  return `${signo}${pct.toFixed(1).replace('.', ',')}%`;
}

/**
 * Centavos → el texto que se muestra en un campo editable.
 *
 * Sin separador de miles, a diferencia de `pesos()`: esto vuelve a entrar por `aCentavos`
 * y un punto de miles ahí es ambiguo. `null` es cadena vacía, no "0,00": un campo vacío y
 * un cero son cosas distintas para un filtro de precio.
 */
export function deCentavos(centavos: number | null): string {
  if (centavos === null) return '';
  return (centavos / 100).toFixed(2).replace('.', ',');
}

/** Pesos escritos por el comerciante → centavos. `null` si no escribió nada usable. */
export function aCentavos(texto: string): number | null {
  const limpio = texto.trim().replace(/\./g, '').replace(',', '.');
  if (limpio === '') return null;
  const valor = Number(limpio);
  if (!Number.isFinite(valor)) return null;
  return Math.round(valor * 100);
}

const ESTADOS_CORRIDA: Readonly<Record<string, string>> = {
  preview: 'Vista previa',
  queued: 'En cola',
  running: 'En curso',
  completed: 'Completada',
  completed_with_errors: 'Completada con errores',
  failed: 'Falló',
  cancelled: 'Cancelada',
  undone: 'Deshecha',
};

export function estadoCorrida(estado: string): string {
  return ESTADOS_CORRIDA[estado] ?? estado;
}

const MOTIVOS: Readonly<Record<string, string>> = {
  sin_cambio: 'El precio no cambia con esta regla',
  modificado_despues: 'El precio cambió después: no lo tocamos',
  ya_tenia_el_precio_anterior: 'Ya tenía el precio anterior',
  variante_borrada: 'La variante ya no existe',
  cancelada: 'Frenaste la actualización antes de llegar acá',
  sin_valor_anterior: 'No había un precio anterior para restaurar',
  missing_price: 'No tiene precio cargado',
  missing_cost: 'No tiene costo cargado',
  missing_promotional_price: 'No tiene precio promocional',
  non_positive_result: 'Quedaría en cero o negativo',
  invalid_margin: 'Ese margen no da un precio posible',
};

export function motivo(codigo: string | null): string {
  if (codigo === null) return '';
  return MOTIVOS[codigo] ?? codigo;
}
